import { AxiosError, AxiosRequestConfig } from 'axios';
import { refreshToken } from 'src/api/authorize';
import client from '../clients/rosterClient';
import apiTokenClient from '../clients/apiTokenClient';
import rrhhClient from '../clients/rrhhClient';
import sgigoClient from '../clients/sgigoClient';
import userManagerClient from '../clients/userManagerClient';
import mailingClient from '../clients/mailingClient';
import blobStorageClient from '../clients/blobStorageClient';
import setAuthorizationToken from '../setAuthorizationToken';
import { httpErrorNotification } from 'src/api/httpErrorNotification';
import AuthContext from 'src/contexts/authentication/AuthProvider';
import { useContext, useLayoutEffect, useRef } from 'react';

interface RetryConfig extends AxiosRequestConfig {
  retry?: boolean;
}
export const AxiosConfigurator: React.FC = () => {
  const { logout } = useContext(AuthContext);
  const isRefreshingRef = useRef<boolean>(false);

  let e401resolved = true;
  useLayoutEffect(() => {
    const handle401 = async (err: AxiosError, axiosInstance: typeof client) => {
      const config: RetryConfig = err.config ?? { retry: false };
      if (err.response?.status === 401 && e401resolved) {
        if (client.defaults.headers.common.Authorization && !config.retry) {
          isRefreshingRef.current = true;
          config.retry = true;
          try {
            const response = await refreshToken();

            if (response && config.headers) {
              setAuthorizationToken();
              config.headers.Authorization =
                client.defaults.headers.common.Authorization;
              isRefreshingRef.current = false;
              return axiosInstance(config);
            }
          } catch (refreshError) {
            isRefreshingRef.current = false;
            logout();
            return Promise.reject(refreshError);
          }
        }
      }

      if (err.response?.status === 401) {
        logout();
        return Promise.reject();
      }
      httpErrorNotification(err);
      return Promise.reject(err);
    };
    const responseIntercept = client.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, client)
    );

    const responseInterceptApiToken = apiTokenClient.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, apiTokenClient)
    );

    const responseInterceptRrhh = rrhhClient.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, rrhhClient)
    );

    const responseInterceptSgigo = sgigoClient.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, sgigoClient)
    );

    const responseInterceptUserManager =
      userManagerClient.interceptors.response.use(
        (response) => response,
        (err: AxiosError) => handle401(err, userManagerClient)
      );

    const responseInterceptMailing = mailingClient.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, mailingClient)
    );

    const responseInterceptBlobStorage =
      blobStorageClient.interceptors.response.use(
        (response) => response,
        (err: AxiosError) => handle401(err, blobStorageClient)
      );

    return () => {
      client.interceptors.response.eject(responseIntercept);
      apiTokenClient.interceptors.response.eject(responseInterceptApiToken);
      rrhhClient.interceptors.response.eject(responseInterceptRrhh);
      sgigoClient.interceptors.response.eject(responseInterceptSgigo);
      userManagerClient.interceptors.response.eject(
        responseInterceptUserManager
      );
      mailingClient.interceptors.response.eject(responseInterceptMailing);
      blobStorageClient.interceptors.response.eject(
        responseInterceptBlobStorage
      );
    };
  }, [logout]);
  return null;
};
